import React, { useState, useEffect } from 'react';
import { ApiService } from '../../services/api';

interface GovernanceStats {
  current_fee_rate: number;
  active_proposals: number;
  treasury_balance: string;
  total_locked_tokens: string;
  total_voting_power: string;
}

interface LockRecord {
  id: number;
  user_wallet: string;
  locked_amount: string;
  lock_duration: string;
  voting_power: string;
  locked_at: string;
  expires_at: string;
  is_active: boolean;
}

const AdminGovernance: React.FC = () => {
  const [stats, setStats] = useState<GovernanceStats | null>(null);
  const [locks, setLocks] = useState<LockRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadGovernanceData();
  }, []);

  const loadGovernanceData = async () => {
    try {
      setLoading(true);
      const [statsData, locksData] = await Promise.all([
        ApiService.getGovernanceStats(),
        ApiService.getAllLocks()
      ]);
      setStats(statsData);
      setLocks(locksData);
    } catch (error) {
      console.error('Failed to load governance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const months = Math.floor(days / 30);
    const years = Math.floor(months / 12);
    
    if (years > 0) return `${years} year${years > 1 ? 's' : ''}`;
    if (months > 0) return `${months} month${months > 1 ? 's' : ''}`;
    return `${days} day${days > 1 ? 's' : ''}`;
  };

  const formatNumber = (num: string) => {
    return new Intl.NumberFormat().format(parseInt(num));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  const isExpired = (expiresAt: string) => {
    return new Date() > new Date(expiresAt);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white">Loading governance data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Governance Dashboard</h1>
        
        {/* Governance Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-2">Current Fee Rate</h3>
            <p className="text-2xl font-bold text-blue-400">
              {(stats?.current_fee_rate || 0) * 100}%
            </p>
          </div>
          
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-2">Active Proposals</h3>
            <p className="text-2xl font-bold text-green-400">
              {stats?.active_proposals || 0}
            </p>
          </div>
          
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-2">Treasury Balance</h3>
            <p className="text-2xl font-bold text-yellow-400">
              ${formatNumber(stats?.treasury_balance || '0')}
            </p>
          </div>
          
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-2">Total Voting Power</h3>
            <p className="text-2xl font-bold text-purple-400">
              {formatNumber(stats?.total_voting_power || '0')}
            </p>
          </div>
        </div>

        {/* Lock Records Table */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Token Locks</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4">User Wallet</th>
                  <th className="text-left py-3 px-4">Locked Amount</th>
                  <th className="text-left py-3 px-4">Duration</th>
                  <th className="text-left py-3 px-4">Voting Power</th>
                  <th className="text-left py-3 px-4">Locked At</th>
                  <th className="text-left py-3 px-4">Expires At</th>
                  <th className="text-left py-3 px-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {locks.map((lock) => (
                  <tr key={lock.id} className="border-b border-gray-700">
                    <td className="py-3 px-4">
                      <div className="text-sm">
                        {lock.user_wallet.slice(0, 8)}...{lock.user_wallet.slice(-8)}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm font-medium">
                        {formatNumber(lock.locked_amount)} LOOP
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm">
                        {formatDuration(parseInt(lock.lock_duration))}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm font-medium text-green-400">
                        {formatNumber(lock.voting_power)}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm text-gray-400">
                        {formatDate(lock.locked_at)}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm text-gray-400">
                        {formatDate(lock.expires_at)}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        isExpired(lock.expires_at) 
                          ? 'bg-red-900 text-red-300' 
                          : lock.is_active 
                            ? 'bg-green-900 text-green-300' 
                            : 'bg-gray-900 text-gray-300'
                      }`}>
                        {isExpired(lock.expires_at) 
                          ? 'Expired' 
                          : lock.is_active 
                            ? 'Active' 
                            : 'Inactive'
                        }
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {locks.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                No token locks found
              </div>
            )}
          </div>
        </div>

        {/* Governance Information */}
        <div className="bg-gray-800 rounded-lg p-6 mt-8">
          <h2 className="text-xl font-semibold mb-4">Governance Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium mb-2">Voting Power Calculation</h3>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>• Voting power = Locked amount × (lock duration / max duration)</li>
                <li>• Maximum lock duration: 4 years</li>
                <li>• Minimum lock duration: 1 month</li>
                <li>• Voting power decreases linearly as lock time decreases</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-medium mb-2">Governance Features</h3>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>• Lock LOOP tokens to receive wLOOP (non-transferable)</li>
                <li>• wLOOP provides voting power for governance proposals</li>
                <li>• Longer locks provide more voting power</li>
                <li>• Tokens are locked until expiration</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminGovernance;





